// SF1 — the score, what capped it, and the campaigns behind it.
//
// Three figures sit together on purpose. The score is money-weighted, so it
// says what the account is worth as a whole. The cap says when a weighted
// average was flattering it. The healthy-spend share says the same thing in a
// sentence that needs no arithmetic to trust.
import React from 'react';
import s from '../AdsHealth.module.css';
import { BAND_TONE, LABEL_TONE, money, pct, ratio } from '../format';

function History({ rows }) {
  const points = (rows || []).filter((r) => r.score != null);
  if (points.length < 2) {
    return (
      <p className={s.scoreMethod}>
        {points.length ? 'One week recorded so far. The trend line starts from the second.'
                       : 'No weekly history yet — it starts from this week.'}
      </p>
    );
  }
  const top = Math.max(...points.map((p) => p.score), 100);
  return (
    <>
      <div className={s.sparkRow}>
        {points.map((p, i) => (
          <div key={p.week_start}
               className={`${s.sparkBar} ${i === points.length - 1 ? s.sparkBar_last : ''}`}
               style={{ height: `${Math.max(3, (p.score / top) * 100)}%` }}
               title={`Week of ${p.week_start}: ${p.score}`} />
        ))}
      </div>
      <div className={s.sparkLabels}>
        {points.map((p) => (
          <span key={p.week_start} className={s.sparkLabel}>
            {p.week_start.slice(5)}
          </span>
        ))}
      </div>
    </>
  );
}

export default function Score({ data, currency }) {
  if (!data) return <p className={s.empty}>Loading…</p>;
  const a = data.account_score || {};

  if (a.score == null) {
    return (
      <div>
        <h3 className={s.sectionTitle}>Not enough data yet</h3>
        <p className={s.scoreMethod}>{a.why}</p>
        {!!(data.campaign_scores || []).length && (
          <table className={s.table}>
            <thead>
              <tr><th>Campaign</th><th>Spend</th><th>What it still needs</th></tr>
            </thead>
            <tbody>
              {data.campaign_scores.map((c) => (
                <tr key={c.campaign_id}>
                  <td>{c.campaign_name}</td>
                  <td>{money(c.spend, currency)}</td>
                  <td>{(c.low_data_why || []).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className={s.scoreBlock}>
        <div className={s.scoreDial}>
          <span className={s.scoreValue}>{Math.round(a.score)}</span>
          <span className={s.scoreOutOf}>out of 100</span>
          <span className={`${s.scoreBand} ${s[`scoreBand_${BAND_TONE[a.band] || 'muted'}`]}`}>
            {a.band}
          </span>
        </div>

        <div className={s.scoreSide}>
          {a.healthy_spend_share != null && (
            <>
              <div className={s.healthyShare}>
                <span className={s.healthyShareValue}>{pct(a.healthy_spend_share)}</span>
                <span className={s.healthyShareLabel}>
                  of your spend is in healthy campaigns
                  {' '}({money(a.healthy_spend, currency)} of {money(a.spend_scored, currency)})
                </span>
              </div>
              <div className={s.shareTrack}>
                <div className={s.shareFill}
                     style={{ width: `${(a.healthy_spend_share || 0) * 100}%` }} />
              </div>
            </>
          )}
          <p className={s.scoreMethod} style={{ marginTop: 12 }}>{a.method}</p>
          {a.excluded_note && <p className={s.scoreMethod}>{a.excluded_note}</p>}
          {(a.cap_reasons || []).map((reason) => (
            <div key={reason} className={s.capNote}>
              <strong>The score is capped at {Math.round(a.score)}.</strong>{' '}
              It would have read {a.uncapped_score} on the weighted average alone. {reason}
            </div>
          ))}
          <History rows={data.history} />
        </div>
      </div>

      <h3 className={s.sectionTitle} style={{ marginTop: 26 }}>Every campaign</h3>
      <p className={s.coverage}>
        Scored on ACOS (30%), ROAS (20%), trend (15%), conversion (15%), data (10%) and
        waste (10%). A component that cannot be read is left out and the rest re-weighted,
        rather than the campaign being docked for the age of the account.
      </p>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Campaign</th><th>Score</th><th>Spend</th><th>ACOS</th>
              <th>Waste</th><th>Verdict</th><th>Trend</th><th>Not scored on</th>
            </tr>
          </thead>
          <tbody>
            {(data.campaign_scores || []).map((c) => (
              <tr key={c.campaign_id}>
                <td>{c.campaign_name}</td>
                <td>
                  {c.score == null ? '—' : (
                    <span className={s[`scoreBand_${BAND_TONE[c.band] || 'muted'}`]}>
                      {Math.round(c.score)}
                    </span>
                  )}
                </td>
                <td>{money(c.spend, currency)}</td>
                <td>{ratio(c.metrics?.acos, c.metrics?.acos_reason)}</td>
                <td>{c.waste_spend ? money(c.waste_spend, currency) : '—'}</td>
                <td>
                  <span className={s[LABEL_TONE[c.label] || 'muted']} title={c.label_why}>
                    {c.label}
                  </span>
                </td>
                <td title={c.trend_why}>{c.trend || '—'}</td>
                <td style={{ color: '#8A93A3', fontSize: 12 }}>
                  {(c.missing || []).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!!data.target_note && <p className={s.coverage}>{data.target_note}</p>}
    </div>
  );
}
